import { FOCUS_RING_NO_RADIUS } from '@amelio/ui-library';

import type { ReactNode } from 'react';

export interface ChoiceChipOption<T extends string> {
    value: T;
    label: string;
    /** Drawn before the label. Decorative: it carries no accessible name of its own. */
    icon?: ReactNode;
    /**
     * A figure the chip carries after its label. `undefined` means *not known yet*, and the badge
     * stays away rather than claiming zero -- a zero drawn while a count is still loading reads as
     * a statement about the folder. A known zero does show.
     */
    count?: number;
}

export type ChoiceChipTone = 'navy' | 'warning';
export type ChoiceChipShape = 'pill' | 'round';

interface ChoiceChipsProps<T extends string> {
    options: ChoiceChipOption<T>[];
    /** The values currently picked; one at most for a single-choice question. */
    selected: readonly T[];
    /** Called with the chip that was pressed; the owner decides whether it adds or replaces. */
    onToggle: (value: T) => void;
    /** Names the group for assistive technology: the question the chips answer. */
    ariaLabel: string;
    tone?: ChoiceChipTone;
    shape?: ChoiceChipShape;
    disabled?: boolean;
    dataTestId?: string;
}

/**
 * The chips share the surface, the ring and the transition; only the shape, the type size and the
 * picked tone differ. The round shape is a 38.5px target, the pill a 31.5px one -- both above the
 * 24px floor.
 *
 * `FOCUS_RING_NO_RADIUS`, because `FOCUS_RING` carries a `rounded-lg` that Tailwind emits after
 * `rounded-full`: a chip that names its own radius loses it in the cascade, silently. The type size
 * lives in `CHIP_SHAPE` alone for the same reason -- two size classes on one element and the
 * emission order, not the author, picks the winner.
 */
const CHIP_BASE = `${FOCUS_RING_NO_RADIUS} inline-flex items-center justify-center gap-2 border font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60`;

const CHIP_SHAPE: Record<ChoiceChipShape, string> = {
    pill: 'min-h-9 rounded-full px-4 py-2 text-[12px]',
    round: 'size-11 rounded-full text-[14px] font-semibold',
};

const CHIP_IDLE = 'border-growth-line bg-white text-navy hover:border-navy';

/** The count badge borrows its contrast from the chip it sits on. */
const BADGE_BASE =
    'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-[11px]';
const BADGE_IDLE = `${BADGE_BASE} bg-growth-tint text-navy`;
const BADGE_PICKED = `${BADGE_BASE} bg-white/25 text-white`;

const CHIP_PICKED: Record<ChoiceChipTone, string> = {
    navy: 'border-navy bg-navy text-white shadow-sm',
    warning: 'border-growth-blocker bg-growth-blocker text-white shadow-sm',
};

/**
 * A row of toggle chips answering one question of the aspirations form: each chip is a native
 * button carrying its whole surface and announcing its state through `aria-pressed`, so the
 * keyboard reaches every chip and Space or Enter flips it. Single-choice questions hand a
 * one-element `selected`; multi-choice questions hand the whole list.
 */
export const ChoiceChips = <T extends string>({
    options,
    selected,
    onToggle,
    ariaLabel,
    tone = 'navy',
    shape = 'pill',
    disabled = false,
    dataTestId = 'choice-chips',
}: ChoiceChipsProps<T>) => (
    <div
        role={'group'}
        aria-label={ariaLabel}
        className={'flex flex-wrap gap-2'}
        data-testid={dataTestId}
    >
        {options.map((option) => {
            const isPicked = selected.includes(option.value);

            return (
                <button
                    key={option.value}
                    type={'button'}
                    aria-pressed={isPicked}
                    disabled={disabled}
                    onClick={() => onToggle(option.value)}
                    className={`${CHIP_BASE} ${CHIP_SHAPE[shape]} ${isPicked ? CHIP_PICKED[tone] : CHIP_IDLE}`}
                    data-testid={`${dataTestId}-${option.value}`}
                >
                    {option.icon}
                    {option.label}
                    {option.count !== undefined && (
                        <span className={isPicked ? BADGE_PICKED : BADGE_IDLE}>{option.count}</span>
                    )}
                </button>
            );
        })}
    </div>
);

/** Flips one value in a list, for a multi-choice question. */
export const toggleInList = <T extends string>(list: readonly T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

/** Picks a value, or clears it when it was already picked, for an optional single-choice question. */
export const toggleSingle = <T extends string>(current: T | null, value: T): T | null =>
    current === value ? null : value;
